import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import ReusableMuiTable from "../../components/common/Table/Table";
import { getFAQ , deleteFAQById } from "../../api/service/faq.service";
import { showToast } from "../../components/common/ToastNotification/ToastNotification";


const Faq = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchFAQData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getFAQ({ page, limit: 10 });
      setFaqs(res?.blogs || []);
      setTotalPages(res?.lastPage || 1);
    } catch (error) {
      console.error("Failed to fetch faqs:", error);
    } finally {
      setLoading(false);
    }
  }, [page]);


  useEffect(() => {
    fetchFAQData();
  }, [fetchFAQData]);

  const addfaq = () => navigate("/add-faq");

  const handleEdit = useCallback((rowData) => {
    const id = rowData.id || rowData.industryTag;
    navigate(`/update-faq/${id}`);
  }, [navigate]);

  const handleDelete = useCallback((rowData) => {
    const id = rowData.id || rowData._id;
    Swal.fire({
      title: "Are you sure to delete this faq ?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#C39B22",
      cancelButtonColor: "#d32f2f",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {

         let result =  await deleteFAQById(id);

          Swal.fire({
            title: "Deleted!",
            text: "FAQ has been deleted successfully.",
            icon: "success",
            confirmButtonColor: "#C39B22",
          });
          fetchFAQData();
          showToast.success("faq deleted successfully");
        } catch (error) {
          console.error("Delete error:", error);
          showToast.error("Failed to delete the faq. Please try again.");

          Swal.fire({
            title: "Error!",
            text: "Something went wrong while deleting.",
            icon: "error",
            confirmButtonColor: "#C39B22",
          });
        }
      }
    });
  }, [fetchFAQData]);

  const columns = useMemo(() => [
    {
      name: "question",
      label: "Question",
      options: {
        customBodyRender: (value) => (
          <Box sx={{
            maxWidth: "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            {value}
          </Box>
        ),
      },
    },
    {
      name: "answer",
      label: "Answer",
      options: {
        customBodyRender: (value) => (
          <Box sx={{
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            {value}
          </Box>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowData = faqs[tableMeta.rowIndex];
          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  sx={{ color: "#000" }}
                  onClick={() => handleEdit(rowData)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "#d32f2f" }}
                  onClick={() => handleDelete(rowData)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ], [faqs, handleEdit, handleDelete]);

  return (
    <>
      <ReusableMuiTable
        title="FAQ Listing"
        data={faqs}
        columns={columns}
        loading={loading}
        options={{
          page: page - 1,
          count: totalPages * 10,
          rowsPerPage: 10,
          onChangePage: (newPage) => setPage(newPage + 1),
        }}
        customToolbarRight={
          <Button
            variant="contained"
            onClick={addfaq}
            sx={{
              backgroundColor: "#C39B22",
              textTransform: "none",
              fontWeight: 600,
              height: "36px",
              "&:hover": {
                backgroundColor: "#b18a1f",
              },
            }}
          >
            Add faq
          </Button>
        }
      />
    </>
  );
};

export default Faq;